import axios from 'axios';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useMutation } from 'react-query';
import useGlobalStore from 'store/global';
import LiveStreamComments from 'components/common/LiveStreamComments';
import {
  Language,
  MediaType,
  Post,
  PostState,
  PostType,
  PostParentKey,
  TippedInfoDetail,
  LiveStreamStatus,
} from 'common/interfaces/api';
import { increaseViewCount, lockTextContent, timeSince } from 'common/utils';
import useStore from 'store/timeline';
import Icon from 'components/common/Icon';
import ThreeDotsMenu from 'components/Post/ThreeDotsMenu';
import UserInfo from 'components/common/UserInfo';
import TimelineSection from 'components/TimelineSection';
import LockedMessage, { LockedType } from 'components/common/LockedMessage';
import RichContent from 'components/common/RichContent';
import { useMediaQuery } from 'common/utils';
import styles from './PostDetails.module.scss';
import LitePost from 'components/LitePost';
import MediaContent from 'components/MediaContent';
import { getPostDetail } from 'service/post';
import LabelButton from 'components/common/LabelButton';
import TippedListOfPost from './TippedListOfPost';
import TippedListDialog from 'components/common/TippedListDialog';
import PostTopicItem from 'components/common/PostTopicItem';
import NewPostReActions from 'components/common/NewPostReActions';
import dayjs from 'lib/dayjs';
import CommentsSection from 'components/CommentsSection';
import PaidLabel from 'components/common/PaidLabel';
import { track } from '@amplitude/analytics-browser';
import { ScreenName } from 'common/utils/pp_tracking';
import { checkIsContentLocked, checkIsPaidPost } from 'common/utils/post';
import Link from 'next/link';
export type Props = {
  post: Post;
  tippedListOfPost: TippedInfoDetail[];
  tippedCount: number;
  loadedPostWithAuth: boolean;
};

function PostDetails({
  post,
  tippedListOfPost,
  tippedCount,
  loadedPostWithAuth = false,
}: Props): JSX.Element {
  // Local post state so that edited content is shown immediately
  const [localPost, setLocalPost] = useState(post);
  const [openTippedList, setOpenTippedList] = useState(false);
  const showTippingDialog = useGlobalStore((state) => state.showTippingDialog);
  const currentUser = useStore((state) => state.currentUser);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const refetchPost = useStore((state) => state.refetchPost);
  const refetchProfile = useStore((state) => state.refetchProfile);
  const router = useRouter();
  const isMobileScreen = useMediaQuery('(max-width: 767px)');
  const showCreatePostDialog = useGlobalStore(
    (state) => state.showCreatePostDialog
  );
  const [reloadingLiveChat, setReloadingLiveChat] = useState(false);
  const [canSeeFreeMinutes, setCanSeeFreeMinutes] = useState(false);
  const [userWantShowComments, setUserWantShowComments] = useState(0);
  const requestToken = useGlobalStore((state) => state.requestToken);
  const showCreatePostDialogMobile = useGlobalStore(
    (state) => state.showCreatePostDialogMobile
  );
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const [sharedPost, setSharedPost] = useState<Post>(null);
  const isOwnPost = localPost.is_my_post;
  const isContentLocked = checkIsContentLocked(localPost);
  const showingPrimeSubBtnHeader =
    !isOwnPost &&
    loadedPostWithAuth &&
    localPost.type === PostType.Free &&
    localPost.user_info?.is_creator &&
    localPost.user_info?.is_open_for_subscription &&
    !localPost.is_subscription;

  const showLogin = useGlobalStore((state) => state.showLogin);

  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage
  );

  // post props may change
  useEffect(() => {
    setLocalPost(post);
    setUserWantShowComments(0);
  }, [post]);

  const trackAmpEvent = (
    eventName: 'Click Read More In Post Detail' | 'Click Thank Post Button'
  ) => {
    track(eventName, {
      post_type: post.type,
      media_type: post.media?.[0]?.type,
      comment_count: post.comment_count,
      like_count: post.like_count,
      post_user_id: post.user_info?.id?.toString(),
      is_followed: post.user_info?.is_followed,
    });
  };

  const isStreaming =
    localPost.streaming?.id &&
    localPost.streaming?.status === LiveStreamStatus.InLive &&
    !!localPost.streaming?.path_hls;

  useEffect(() => {
    if (
      !localPost.media ||
      !localPost.media[0] ||
      localPost.media[0].type !== MediaType.Link
    ) {
      setSharedPost(null);
      return;
    }
  }, [localPost]);

  const { mutate: unlockPost, isLoading: isUnlocking } = useMutation(
    () => {
      return axios.get(`/posts/${localPost.id}?use_token=true`);
    },
    {
      onSuccess: () => {
        refetchProfile();
        refetchPost();
      },
      onError: () => {
        setSnackbarMessage({
          type: 'error',
          text: 'エラーが発生しました',
        });
      },
    }
  );

  const [isShortMode, setShortMode] = useState(true);
  const [hasShortMode, setHasShortMode] = useState(true);
  const textRef = useCallback(
    (node: Element) => {
      const initShortMode = setTimeout(() => {
        if (node !== null) {
          const scrollHeight = node.scrollHeight;
          const clientHeight = node.clientHeight;
          setHasShortMode(scrollHeight > clientHeight);
        }
      }, 500);
      return () => clearTimeout(initShortMode);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localPost.text]
  );

  useEffect(() => {
    setShortMode(true);
    setUserWantShowComments(0);
    setReloadingLiveChat(true);
    setTimeout(() => {
      setReloadingLiveChat(false);
    }, 2000);
  }, [post?.id]);

  const content = (
    <>
      <div
        className={cn(styles.postText, {
          [styles.locked]: isContentLocked,
          [styles.notShortMode]: !isShortMode,
          [styles.showingLoadmore]:
            !isContentLocked && hasShortMode && isShortMode,
        })}
        ref={textRef}
      >
        <RichContent
          text={
            isContentLocked ? lockTextContent(localPost.text) : localPost.text
          }
        />
        {!isContentLocked && hasShortMode && isShortMode && (
          <div className={styles.readmoreWrapper}>
            <button
              className={styles.readmore}
              onClick={() => {
                trackAmpEvent('Click Read More In Post Detail');
                if (!currentAuth) {
                  showLogin({});
                  return;
                }
                increaseViewCount(localPost.id);
                setShortMode(false);
              }}
            >
              続きを読む
            </button>
          </div>
        )}
        {isContentLocked && <div className={styles.lockedOverlay}></div>}
      </div>
      {sharedPost && (!hasShortMode || !isShortMode) && (
        <div className={styles.sharedPost}>
          <LitePost
            post={sharedPost}
            parent={PostParentKey.PostDetail}
            showReadMore
          />
        </div>
      )}
      {!isContentLocked &&
        hasShortMode &&
        (isShortMode ? (
          <></>
        ) : (
          <button
            onClick={() => {
              setShortMode(true);
              window.scrollTo(0, 0);
            }}
            className={styles.showLess}
          >
            一部を表示
          </button>
        ))}
      {loadedPostWithAuth && (isContentLocked || !currentAuth) && (
        <div style={{ marginBottom: '2px' }}>
          {localPost.type !== PostType.Gold &&
          currentUser &&
          typeof currentUser.referral_token === 'number' &&
          currentUser.referral_token > 0 ? (
            <div className={styles.referralTokenUse}>
              <p>
                招待チケットを使用して、あと{currentUser.referral_token}
                回、無料で{isMobileScreen && <br />}
                プライム投稿をご覧いただけます
              </p>
              {isUnlocking ? (
                <Icon name="loading-anim-3" width={24} height={24} />
              ) : (
                <button onClick={() => unlockPost()}>プライム投稿を見る</button>
              )}
            </div>
          ) : (
            <LockedMessage
              type={LockedType.Text}
              postAuthor={localPost.user_info}
              postType={localPost.type}
            />
          )}
        </div>
      )}
    </>
  );

  // Right side components
  const isScheduledPost = new Date(localPost.created_at) > new Date();
  const timeSinceText = isScheduledPost
    ? `${dayjs(localPost.created_at).format('YYYY/MM/DD HH:mm')}に公開予定`
    : timeSince(new Date(localPost.created_at), Language.Ja);

  const postTopicsList = useMemo(() => {
    if (
      (!localPost.domains || localPost.domains.length === 0) &&
      (!localPost.symbols || localPost.symbols.length === 0)
    )
      return <></>;
    return (
      <div className={styles.postTopicLists}>
        {(localPost.symbols || []).map((symbol) => {
          return (
            <div
              className={styles.item}
              key={`symbol-${symbol.name}`}
              onClick={() => {
                if (!currentAuth) {
                  showLogin({
                    redirectUrl: `/chart?id=${symbol.id}&tab=chart`,
                  });
                  return;
                }
                void router.push(`/chart?id=${symbol.id}&tab=chart`);
              }}
            >
              <PostTopicItem
                text={symbol.name}
                isActive={false}
                color="#fff"
                background="#04b56a"
                fontSize={9}
              />
            </div>
          );
        })}

        {(localPost.domains || []).map((topic) => (
          <Link href={`/topic/${topic.id}`} key={`topic-${topic.id}`}>
            <a
              className={cn(styles.item, {
                [styles.disabled]: topic.is_disabled,
              })}
              rel={topic.is_disabled ? 'nofollow' : ''}
              key={`topic-${topic.id}`}
            >
              <PostTopicItem
                text={topic.name}
                isActive={false}
                color="#000"
                background="#EEEBEB"
                fontSize={9}
              />
            </a>
          </Link>
        ))}
      </div>
    );
  }, [currentAuth, localPost.domains, localPost.symbols, router, showLogin]);

  return (
    <div className={styles.postWrapper}>
      <MediaContent
        localPost={localPost}
        setSharedPost={setSharedPost}
        sharedPost={sharedPost}
        parent={PostParentKey.PostDetail}
        isContentLocked={isContentLocked}
        isPostDetail
        isMobileScreen={isMobileScreen}
        canSeeFreeMinutes={loadedPostWithAuth && canSeeFreeMinutes}
        setCanSeeFreeMinutes={setCanSeeFreeMinutes}
        loadedPostWithAuth={loadedPostWithAuth}
      />
      <div
        className={styles.post}
        style={tippedCount > 0 ? { borderBottom: '1px solid #eee' } : {}}
      >
        <div
          className={cn(styles.header, {
            [styles.showingSubBtn]: showingPrimeSubBtnHeader,
          })}
        >
          <UserInfo
            user={localPost.user_info}
            avatarSize={48}
            showMembershipIcon={false}
            hasFollowBtn={false}
            badgeSize={40}
            showBadge={false}
            isAdsPost={localPost.is_affiliate}
            showLiveFlag
            postedAt={localPost.is_affiliate ? '' : timeSinceText}
          />

          <div className={styles.rightCorner}>
            {showingPrimeSubBtnHeader && (
              <LabelButton
                type="green-1"
                text="プライム登録"
                className={styles.primeSubBtn}
                onClick={() => {
                  if (!currentAuth) {
                    showLogin({});
                    return;
                  }
                  showPaymentDialog({ creator: localPost.user_info });
                }}
              />
            )}
            {loadedPostWithAuth &&
              !isOwnPost &&
              localPost.user_info?.is_creator &&
              localPost.user_info?.is_open_for_subscription && (
                <LabelButton
                  type="pink"
                  className={styles.thanksBtn}
                  onClick={() => {
                    trackAmpEvent('Click Thank Post Button');
                    if (!currentAuth) {
                      showLogin({});
                      return;
                    }
                    showTippingDialog({
                      post: localPost,
                      isStreaming: !!localPost.streaming,
                    });
                  }}
                  text={
                    <>
                      <div className={styles.thanksIcon}>
                        <Icon name="thanks-icon" width={18} height={18} />
                      </div>
                      <span>ありがとう</span>
                    </>
                  }
                />
              )}
            {checkIsPaidPost(localPost.type) && (
              <div className={styles.paidLabel}>
                <PaidLabel
                  size={28}
                  freeMinutesFontSize={9}
                  freeMinutes={localPost.free_minutes || 0}
                  isGoldPost={localPost.type === PostType.Gold}
                  isPlusPost={localPost.type === PostType.Plus}
                />
              </div>
            )}
            {currentUser && (
              <div className={styles.threeDot}>
                <ThreeDotsMenu
                  isOwnPost={isOwnPost}
                  isPostDetail
                  postId={localPost.id}
                  userId={localPost.user_info?.id}
                  isSaved={localPost.is_saved}
                  isStreamingPost={!!localPost.streaming}
                  isAdsPost={localPost.is_affiliate}
                  onDelete={() =>
                    router.push(`/${localPost.user_info?.username || ''}`)
                  }
                  onBlock={() => router.push('/')}
                  isPinned={false}
                  onHide={() => router.push('/')}
                  isGoldPost={localPost.type === PostType.Gold}
                  isProcessing={localPost.status === PostState.OnProcessing}
                  onEdit={async () => {
                    const postData = (await getPostDetail(post.id, true))?.data;
                    if (isMobileScreen) {
                      showCreatePostDialogMobile({
                        post: postData || localPost,
                        setLocalPost: setLocalPost,
                        editMode: true,
                        open: true,
                      });
                    } else {
                      showCreatePostDialog({
                        post: postData || localPost,
                        setLocalPost: setLocalPost,
                        editMode: true,
                        open: true,
                      });
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {(!hasShortMode || !isShortMode) && (
          <div className={styles.actions}>{postTopicsList}</div>
        )}
        {content}
        {openTippedList && (
          <TippedListDialog
            open={openTippedList}
            setOpen={setOpenTippedList}
            postId={localPost.id}
          />
        )}
      </div>
      {!isStreaming && (
        <TippedListOfPost
          tippedList={tippedListOfPost}
          tippedCount={tippedCount}
          setOpenTippedList={setOpenTippedList}
        />
      )}
      {!isStreaming && (
        <NewPostReActions
          post={localPost}
          isPostDetail
          handleClickComment={() =>
            setUserWantShowComments((count) => count + 1)
          }
          showViewCount={!localPost.is_affiliate}
          showNumber
          screen={ScreenName.PostDetail}
        />
      )}
      {isStreaming && <div style={{ borderTop: '1px solid #eee' }}></div>}
      {!isStreaming && (
        <CommentsSection
          localPost={localPost}
          userWantShowComments={userWantShowComments}
        />
      )}
      {currentAuth &&
        isStreaming &&
        localPost &&
        requestToken &&
        (reloadingLiveChat ? (
          <div className={styles.loading}>
            <Icon name="loading-anim-3" width={24} height={24} />
          </div>
        ) : (
          <LiveStreamComments
            className={styles.postStreamComments}
            post={localPost}
            requestToken={requestToken}
            tippedList={tippedListOfPost}
            setOpenTippedList={setOpenTippedList}
            tippedCount={tippedCount}
            isLocked={isContentLocked && !canSeeFreeMinutes}
          />
        ))}

      {relatedPosts(localPost)}
    </div>
  );
}

const relatedPosts = (localPost: Post) => {
  return (
    <div className={styles.relatedPosts}>
      {localPost && localPost.id && (
        <TimelineSection
          apiUrl={`/posts/${localPost.id}/related_posts`}
          queryKey={[PostParentKey.RelatedPosts, localPost.id?.toString()]}
          hideNoResult
          disableInfiniteScroll
        />
      )}
    </div>
  );
};
const MemoizedPostDetails = React.memo(PostDetails);
export default MemoizedPostDetails;
